import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../redux/app.state';
import { CookieService } from 'ngx-cookie-service';
import { logoutUser, updateUser } from '../redux/actions/user.actions';
import { Router } from '@angular/router';
import { User } from 'src/typings';
import { AuthResponse } from './auth.interfaces';
import { clearCart } from '../redux/actions/cart.actions';
import { AuthSessionCoordinatorService } from './auth-session-coordinator.service';

const ACCESS_TOKEN_COOKIE_DAYS = 1;
const REFRESH_TOKEN_COOKIE_DAYS = 90;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private cookieService: CookieService,
    private router: Router,
    private authSessionCoordinator: AuthSessionCoordinatorService
  ) {}

  private isRefreshing = false;

  private refreshTokenSubject = new BehaviorSubject<AuthResponse | null>(null);

  private trackedSessionFingerprint = this.authSessionCoordinator.getCurrentFingerprint();

  private sessionVersion = 0;

  login(phone: string, password: string): Observable<AuthResponse> {
    const loginVersion = this.sessionVersion;
    return this.http
      .post<AuthResponse>(`${environment.BACKEND}/login`, {
        username: phone,
        password: password
      })
      .pipe(
        tap((data) => {
          if (loginVersion !== this.sessionVersion) {
            return;
          }
          this.persistTokens(data);
          this.authSessionCoordinator.publish('login');
        })
      );
  }

  logout(options: { broadcast?: boolean; navigateToLogin?: boolean } = {}): void {
    const authOptions = this.getAuthOption();

    this.http
      .post<void>(`${environment.BACKEND}/auth/logout/user`, {}, authOptions)
      .pipe(
        catchError(() => of(void 0)),
        finalize(() => {
          this.resetClientContext({
            deleteCookies: true,
            broadcast: options.broadcast ?? true
          });

          if (options.navigateToLogin !== false) {
            this.router.navigateByUrl('/login');
          }
        })
      )
      .subscribe();
  }

  getUserByToken(options: { skipRefreshRetry?: boolean } = {}): Observable<User> {
    const headers = options.skipRefreshRetry
      ? this.getAuthOption().headers.set('X-Skip-Refresh-Retry', '1')
      : this.getAuthOption().headers;

    return this.http.get<User>(environment.BACKEND + '/user/profile', { headers });
  }

  syncCurrentUser(options: { skipRefreshRetry?: boolean } = {}): Observable<User> {
    return this.getUserByToken(options).pipe(
      tap((user) => {
        this.store.dispatch(updateUser({ payload: user }));
      })
    );
  }

  restoreSession(): Observable<User> {
    if (!this.hasAuthCookies()) {
      return throwError(() => 'Сессия истекла. Пожалуйста, войдите снова.');
    }

    if (this.getAccessToken()) {
      return this.syncCurrentUser();
    }

    return this.refreshToken().pipe(
      switchMap(() => this.syncCurrentUser({ skipRefreshRetry: true }))
    );
  }

  isAuth(): Observable<boolean> {
    return this.store.pipe(
      map((state) => Boolean(state.user.info) || this.hasAuthCookies()),
      distinctUntilChanged()
    );
  }

  getAuthOption() {
    const token = this.getAccessToken();
    return {
      headers: new HttpHeaders(token ? { Authorization: `Bearer ${token}` } : {})
    };
  }

  refreshToken(): Observable<AuthResponse> {
    const token = this.cookieService.get('refresh_token');

    if (!token) {
      return throwError(() => 'Сессия истекла. Пожалуйста, войдите снова.');
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject = new BehaviorSubject<AuthResponse | null>(null);
      const refreshVersion = this.sessionVersion;
      return this.http
        .post<AuthResponse>(`${environment.BACKEND}/auth/refresh/user`, { refresh_token: token })
        .pipe(
          tap((data) => {
            if (refreshVersion !== this.sessionVersion) {
              return;
            }
            this.persistTokens(data);
            this.refreshTokenSubject.next(data);
            this.authSessionCoordinator.publish('refresh');
          }),
          catchError((error) => {
            this.refreshTokenSubject.error(error);
            this.refreshTokenSubject = new BehaviorSubject<AuthResponse | null>(null);
            return throwError(() => error);
          }),
          finalize(() => {
            this.isRefreshing = false;
          })
        );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((response): response is AuthResponse => response !== null),
        take(1),
        map((response) => response)
      );
    }
  }

  hasAuthCookies() {
    return this.cookieService.check('access_token') || this.cookieService.check('refresh_token');
  }

  getCurrentSessionFingerprint() {
    return this.authSessionCoordinator.getCurrentFingerprint();
  }

  getTrackedSessionFingerprint() {
    return this.trackedSessionFingerprint;
  }

  syncTrackedSessionFingerprint() {
    this.trackedSessionFingerprint = this.getCurrentSessionFingerprint();
    return this.trackedSessionFingerprint;
  }

  hasTrackedSessionMismatch() {
    return this.getTrackedSessionFingerprint() !== this.getCurrentSessionFingerprint();
  }

  resetClientContext(options: { deleteCookies?: boolean; broadcast?: boolean } = {}) {
    const deleteCookies = options.deleteCookies !== false;
    this.sessionVersion += 1;
    this.cancelRefreshQueue('Сессия была изменена');

    if (deleteCookies) {
      this.cookieService.delete('access_token');
      this.cookieService.delete('refresh_token');
    }

    this.store.dispatch(clearCart());
    this.store.dispatch(logoutUser());

    try {
      globalThis.sessionStorage?.clear();
    } catch {
      // noop
    }

    this.trackedSessionFingerprint = deleteCookies ? null : this.getCurrentSessionFingerprint();

    if (options.broadcast) {
      this.authSessionCoordinator.publish(deleteCookies ? 'logout' : 'refresh');
    }
  }

  private getAccessToken() {
    return this.cookieService.get('access_token');
  }

  private persistTokens(data: AuthResponse) {
    this.cookieService.set('access_token', data.access_token, ACCESS_TOKEN_COOKIE_DAYS);
    this.cookieService.set('refresh_token', data.refresh_token, REFRESH_TOKEN_COOKIE_DAYS);
    this.syncTrackedSessionFingerprint();
  }

  private cancelRefreshQueue(message: string) {
    this.isRefreshing = false;
    this.refreshTokenSubject.error(new Error(message));
    this.refreshTokenSubject = new BehaviorSubject<AuthResponse | null>(null);
  }
}
